import * as PIXI from "pixi.js";
import { hitTestRectangle } from "./utils";
import * as Components from "./components";
import Base from "./Base";
import FontFaceObserver from "fontfaceobserver";

export default class Game extends Base {
  constructor(...props) {
    super(...props);

    const app = new PIXI.Application(window.innerWidth, window.innerHeight, {
      transparent: true,
      antialias: true,
      resolution: window.devicePixelRatio
    });

    app.renderer.view.style.position = "absolute";
    app.renderer.view.style.display = "block";
    app.renderer.view.style.width = "100%";
    app.renderer.view.style.height = "100%";
    app.renderer.roundPixels = true;
    // renderer.autoResize = true;

    document.body.appendChild(app.view);

    this.app = app;

    const loader = PIXI.loader;
    for (var key in this.assets) {
      loader.add(key, this.assets[key]);
    }
    this.loader = loader;
  }

  // Show as background, do not start
  preStart() {
    const loader = this.loader;
    this.preStarted = true;

    loader.load((loader, resources) => {
      const app = this.app;
      const { renderer, stage } = app;

      /* Game Scence (Background, Opacity Mask) */
      const gameScence = Components.loadGameScence({
        resources,
        stage,
        renderer
      });
      this.gameScence = gameScence;
      this.mask = this.loadMask({ parent: gameScence, renderer });

      renderer.render(stage);
    });
  }

  start() {
    new FontFaceObserver("Gotham Bold").load().then(() => {
      this.app.ticker.start();

      if (!this.preStarted) {
        this.preStart();
      }

      this.preStarted = false;

      const loader = this.loader;
      loader.load((loader, resources) => {
        const app = this.app;
        const { renderer, stage } = app;
        const gameScence = this.gameScence;
        const mask = this.mask;

        /* Prelude */
        const { preludeScence, countText } = this.loadPreludeScence(
          this.game_prelude_duration,
          {
            texture: resources.count.texture,
            renderer,
            stage,
            resources
          }
        );

        /* Blanket */
        const blanket = this.loadBlanket({
          renderer,
          parent: gameScence,
          texture: resources.blanket.texture
        });

        this.preludeScence = preludeScence;

        let count_left = this.game_prelude_duration;

        renderer.render(stage);

        const pre_game_start_counter = setInterval(() => {
          if (--count_left < 0) {
            clearInterval(pre_game_start_counter);
            preludeScence.destroy();
            mask.destroy();

            // play begin
            this.game_state = "play";
            this.play({ gameScence, resources, blanket });
          } else {
            // count down
            countText.text = count_left;
          }
        }, 1000);
        this.pre_game_start_counter = pre_game_start_counter;
      });
    });
  }

  play({ gameScence, resources, blanket }) {
    const app = this.app;
    const { renderer, stage } = app;

    /* Timer Board */
    const { timerText } = Components.loadTimer({
      parent: gameScence,
      texture: resources.board.texture
    });

    /* Container for Drops (Red Pockets & Fruits) */
    const drops_container = this.loadContainer({
      parent: gameScence
    });

    /* Drops */
    // Fruit
    const fruits_container = this.loadContainer({
      parent: drops_container
    });
    this.loadDrops({
      renderer,
      stage,
      texture: resources.fruit.texture,
      catch_effect_texture: resources.catch_effect.texture,
      parent: fruits_container,
      layout: {
        width: 70,
        height: 85
      },
      vy: this.fruit_speed,
      count: this.fruit_count
    });

    // Coin
    const coins_container = this.loadContainer({
      parent: drops_container
    });
    this.loadDrops({
      renderer,
      stage,
      texture: resources.coin.texture,
      catch_effect_texture: resources.catch_effect.texture,
      parent: coins_container,
      layout: {
        width: 60,
        height: 60
      },
      vy: this.coin_speed,
      count: this.coin_count
    });

    // Red Pocket
    const red_pockets_container = this.loadContainer({
      parent: drops_container
    });
    this.loadDrops({
      renderer,
      stage,
      texture: resources.redpocket.texture,
      catch_effect_texture: resources.catch_effect.texture,
      parent: red_pockets_container,
      layout: {
        width: 50,
        height: 65
      },
      vy: this.red_pocket_speed,
      count: this.red_pocket_count
    });

    /* Sort Children's Z-Index */
    stage.children.sort((a, b) => {
      a.zIndex = a.zIndex || 0;
      b.zIndex = b.zIndex || 0;
      return a.zIndex - b.zIndex;
    });
    gameScence.children.sort((a, b) => {
      a.zIndex = a.zIndex || 0;
      b.zIndex = b.zIndex || 0;
      return a.zIndex - b.zIndex;
    });

    this.game_will_end_at = Date.now() + this.game_duration;

    app.ticker.start();
    app.ticker.add(
      this.playLoop.bind(this, {
        gameScence,
        red_pockets_container,
        fruits_container,
        coins_container,
        timerText,
        blanket,
        resources
      })
    );
  }

  playLoop(
    {
      gameScence,
      red_pockets_container,
      fruits_container,
      coins_container,
      timerText,
      blanket,
      resources
    },
    deltaSpeed
  ) {
    const app = this.app;
    const { renderer, stage } = app;

    const now = Date.now();

    const red_pockets = red_pockets_container.children;
    this.hitTest(red_pockets, blanket, deltaSpeed, function() {
      this.score.red_pocket += 1;
    });

    const fruits = fruits_container.children;
    this.hitTest(fruits, blanket, deltaSpeed, function() {
      this.score.fruit += 1;
    });

    const coins = coins_container.children;
    this.hitTest(coins, blanket, deltaSpeed, function() {
      this.score.coin += 1;
    });

    const timeout = now > this.game_will_end_at;

    this.updateTimer({ now, element: timerText, timeout });

    if (timeout) {
      // Game over
      this.app.ticker.stop(this.playLoop);

      this.finish({ resources, renderer, stage, gameScence });
    }
  }

  finish({ resources, renderer, stage, gameScence }) {
    if (this.game_state === "play") {
      this.gameoverScence = this.loadGameoverScence({
        texture: resources.gameover.texture,
        renderer,
        stage,
        onTapGameover: this.onTapGameover
      });

      /* Gameover Mask */
      this.loadMask({ parent: gameScence, renderer });
      this.game_state = "stop";

      this.onGameover();
    }

    renderer.render(stage);
  }

  updateTimer({ now, element, timeout }) {
    if (!timeout) {
      const time_to_live = Math.round(
        (this.game_will_end_at - now) / 1000
      ).toString();

      if (element.text !== time_to_live) {
        element.text = time_to_live;
      }
    } else {
      element.text = 0;
    }
  }

  letItRain({ count, callback }) {
    while (count--) {
      setTimeout(callback, Math.round(Math.random() * this.game_duration));
    }
  }

  hitTest(drops, blanket, delta, onCatchCallback) {
    const vh = this.app.renderer.screen.height;

    for (var i = drops.length - 1; i >= 0; i--) {
      const drop = drops[i];

      drop.y += drop.vy * delta;
      drop.vy += drop.vy * delta * this.gravity;

      // Catched by Blanket?
      if (!drop.catched && hitTestRectangle(drop, blanket)) {
        drop.catched = true;

        onCatchCallback.call(this);

        // show catch effect
        drop.showCatchEffect({ app: this.app });
        // drop.destroy();
      } else {
        // Reach Screen Bottom?
        if (drop.y >= vh) {
          drop.destroy();
        }
      }
    }
  }

  getScore() {
    return this.score;
  }

  destroy() {
    this.app.destroy();
  }

  // This method will re-initialize the game for the next round
  reset() {
    clearInterval(this.pre_game_start_counter);

    this.preludeScence && this.preludeScence.destroy();
    this.gameScence && this.gameScence.destroy();
    this.gameoverScence && this.gameoverScence.destroy();

    this.app.ticker.stop();

    const { renderer, stage } = this.app;
    renderer.render(stage);
  }
}
